import type React from 'react';

import { Box, Grid } from '@mui/material';
import { OrdIconStandard, ServiceItem } from '@ragu-components/ord-components';
import { PersonalAccessToken } from '@ragu-components/ord-resources';
import {
  UserSettings,
  ChangePassword,
  Totp,
  VoicemailSettings,
  CallForwardingSettings,
} from '@ragu-components/ord-views';
import { useCreatePath, useTranslate } from 'react-admin';

import { PersonalAccessTokensCreateInDialogButton } from 'src/views/Resources/personal';

export const Settings = (): React.ReactElement => {
  const translate = useTranslate();
  const createPath = useCreatePath();
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        // Fill the visible frame (viewport minus the AppBar offset RaLayout
        // reserves via `marginTop: theme.spacing(6)`) so this Box is a real
        // scroll container. The body has `overflow-y: hidden`, so without an
        // explicit height nothing here would scroll on short viewports.
        height: (theme) => `calc(100dvh - ${theme.spacing(6)})`,
        width: '100%',
        overflowY: 'auto',
      }}
    >
      <Grid container spacing={2} justifyContent='center' sx={{ maxWidth: 'lg', width: '100%', my: 'auto', py: 2 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <ServiceItem
            buttonLabel={translate('ord.settings.userSettings.settingButtonLabel')}
            description={translate('ord.settings.userSettings.description')}
            icon={<OrdIconStandard name='userPolicy' />}
            name={translate(`ord.settings.userSettings.title`)}
            to={<UserSettings />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <ServiceItem
            buttonLabel={translate('ord.settings.changePassword.settingButtonLabel')}
            description={translate('ord.settings.changePassword.description')}
            icon={<OrdIconStandard name='password' />}
            name={translate(`ord.settings.changePassword.title`)}
            to={<ChangePassword />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <ServiceItem
            buttonLabel={translate('ord.settings.totp.settingButtonLabel')}
            description={translate('ord.settings.totp.description')}
            icon={<OrdIconStandard name='pin' />}
            name={translate(`ord.settings.totp.title`)}
            to={<Totp />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <ServiceItem
            buttonLabel={translate('resources.voicemails.settingButtonLabel')}
            description={translate('resources.voicemails.description')}
            icon={<OrdIconStandard name='voicemail' />}
            name={translate('resources.voicemails.name')}
            to={<VoicemailSettings />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <ServiceItem
            buttonLabel={translate('callForwarding.settingButtonLabel')}
            description={translate('callForwarding.description')}
            icon={<OrdIconStandard name='rules' />}
            name={translate('ord.settings.callForwarding.title')}
            to={<CallForwardingSettings />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <ServiceItem
            buttonLabel={translate('ra.action.show')}
            description={translate('resources.me/personal_access_tokens.description')}
            icon={<OrdIconStandard name='token' />}
            name={translate('resources.me/personal_access_tokens.name')}
            shortcutLink={
              <PersonalAccessTokensCreateInDialogButton
                ButtonProps={{
                  sx: {
                    minWidth: 0,
                    width: 'fit-content',
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    textTransform: 'none',
                    paddingX: 0,
                    '& .MuiButton-startIcon': {
                      display: 'none',
                    },
                  },
                }}
                label='resources.me/personal_access_tokens.operations.create'
              />
            }
            to={createPath({ resource: `me/${PersonalAccessToken}`, type: 'list' })}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
